import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { policies } from '../data/policies';

const privacyPolicy = (
  <div className="space-y-6 text-black">
    <p>Harpalyk Engineering Private Limited ("Harpalyk", "we", "us") respects your privacy. This policy explains what information we collect on harpalyk.com and how we use it.</p>
    <h3 className="text-2xl font-light text-gray-900 mt-8 mb-4">Information We Collect</h3>
    <ul className="list-disc pl-6 space-y-2">
      <li><strong>Contact details</strong> you submit through our contact, demo, enquiry or dealer registration forms (name, email, phone, city, message).</li>
      <li><strong>Order information</strong> when you purchase a product (billing details processed securely by Razorpay — we do not store card numbers).</li>
      <li><strong>Usage data</strong> such as pages visited and device type, collected via analytics cookies to improve the website.</li>
    </ul>
    <h3 className="text-2xl font-light text-gray-900 mt-8 mb-4">How We Use Your Information</h3>
    <ul className="list-disc pl-6 space-y-2">
      <li>To respond to enquiries, schedule demos and process orders.</li>
      <li>To provide installation, warranty and after-sales support.</li>
      <li>To improve our products, website and customer experience.</li>
      <li>To send service updates you have requested. We do not sell your personal data to third parties.</li>
    </ul>
    <h3 className="text-2xl font-light text-gray-900 mt-8 mb-4">Data Security & Retention</h3>
    <p>Your data is stored on secured servers and shared only with service providers required to fulfil your request (e.g., payment gateway, courier). We retain enquiry data only as long as needed to serve you.</p>
    <h3 className="text-2xl font-light text-gray-900 mt-8 mb-4">Your Rights</h3>
    <p>You may request access, correction or deletion of your personal data at any time by writing to <strong>support@harpalyk.com</strong>.</p>
    <h3 className="text-2xl font-light text-gray-900 mt-8 mb-4">Contact</h3>
    <p>Harpalyk Engineering Private Limited, Gurugram, Haryana, India · +91-7011370322 · support@harpalyk.com</p>
  </div>
);

export const POLICY_PAGES = {
  '/privacy-policy': { title: 'Privacy Policy', content: privacyPolicy },
  '/terms-of-service': { title: 'Terms of Service', content: policies.termsOfService },
  '/shipping-policy': { title: 'Shipping Policy', content: policies.shippingPolicy },
  '/warranty-policy': { title: 'Warranty Policy', content: policies.warrantyPolicy },
  '/return-refund-policy': { title: 'Return & Refund Policy', content: policies.returnPolicy },
  '/customer-policy': { title: 'Customer Policy', content: policies.customerPolicy },
  '/cookie-policy': { title: 'Cookie Policy', content: policies.cookiePolicy },
};

export default function PolicyPage() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const page = POLICY_PAGES[pathname] || POLICY_PAGES['/privacy-policy'];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{page.title} | Harpalyk</title>
        <link rel="canonical" href={`https://harpalyk.com${pathname}`} />
      </Helmet>
      <Header scrolled={scrolled} />
      <main className="pt-16 lg:pt-[72px]" data-testid="policy-page">
        <Breadcrumbs items={[{ label: page.title }]} />
        <section className="py-14 lg:py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900 mb-10">{page.title}</h1>
            <div className="prose-policy">{page.content}</div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
